import React, { useState, useEffect, useContext, useRef } from "react";
import styled from "styled-components";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
} from "src/components";
import { UserContext } from "src/scenes/App/UserContext";
import { UserKeys } from "src/constants/userDetails";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import Skeleton from "src/scenes/Statistics/components/Skeleton";
import moment from "moment";
import { SurveyStatusType } from "src/constants/survey";
import { RealTimeBadge } from "./RealTimeBadge";

const PollResultsContent = styled.div`
    width: 100%;
    max-width: 100%;
    min-width: 0;
    display: grid;
    gap: 1.1rem;
    padding: 1.2rem 1.6rem 1.5rem;
    box-sizing: border-box;
`;

const PollDetailSection = styled.div`
    width: 100%;
    min-width: 0;
    display: grid;
    gap: 0.9rem;
    padding: 1rem;
    border: 1px solid var(--calendar-border, #D8DEEC);
    border-radius: var(--calendar-radius-card, 12px);
    background: rgba(243, 245, 251, 0.95);
    box-sizing: border-box;
`;

const PollMediaWrap = styled.div`
    width: 100%;
    min-width: 0;
    max-width: 100%;
    position: relative;
`;

const PollMediaFrame = styled.div`
    width: 100%;
    min-width: 0;
    max-width: 100%;
    aspect-ratio: 16 / 9;
    position: relative;
    overflow: hidden;
    background: #EEF3F8;
    border-radius: var(--calendar-radius-card, 12px);
`;

const PollImage = styled.img`
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: center;
`;

const CountdownBadge = styled.div`
    position: absolute;
    left: 50%;
    bottom: 12px;
    transform: translateX(-50%);
    max-width: calc(100% - 24px);
    padding: 8px 12px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.76);
    color: #fff;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8rem;
    font-weight: 900;
    line-height: 1.2;
    text-align: center;
    white-space: nowrap;
`;

const PollContentStack = styled.div`
    min-width: 0;
    display: grid;
    gap: 14px;
`;

const PollTitle = styled.h3`
    color: var(--calendar-text, #171A22);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1.25rem;
    font-weight: 800;
    line-height: 1.25;
    margin: 0;
    overflow-wrap: anywhere;
`;

const PollEndDate = styled.div`
    color: var(--calendar-text-muted, #6A7387);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8rem;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
`;

const VoteTally = styled.div`
    justify-self: start;
    padding: 7px 10px;
    border-radius: 999px;
    background: rgba(91, 110, 245, 0.12);
    color: var(--calendar-accent, #5B6EF5);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.78rem;
    font-weight: 900;
    line-height: 1.2;
`;

const PollOptionsList = styled.div`
    min-width: 0;
    display: grid;
    gap: 8px;
`;

const AnswerOption = styled.div`
    min-width: 0;
    min-height: 48px;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid ${p => p.$topVote ? "var(--calendar-accent, #5B6EF5)" : "var(--calendar-border, #D8DEEC)"};
    border-radius: var(--calendar-radius-card, 12px);
    background:
        linear-gradient(
            90deg,
            rgba(91, 110, 245, 0.16) 0%,
            rgba(91, 110, 245, 0.16) ${p => p.$percentage || 0}%,
            var(--calendar-surface-secondary, #F3F5FB) ${p => p.$percentage || 0}%,
            var(--calendar-surface-secondary, #F3F5FB) 100%
        );
    color: var(--calendar-text, #171A22);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9rem;
    font-weight: 800;
    line-height: 1.3;
    box-sizing: border-box;
`;

const AnswerNumber = styled.div`
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: var(--calendar-accent-soft, rgba(91, 110, 245, 0.13));
    color: var(--calendar-accent, #5B6EF5);
    font-size: 0.78rem;
    font-weight: 900;
`;

const AnswerText = styled.span`
    min-width: 0;
    overflow-wrap: anywhere;
`;

const PercentagePill = styled.span`
    padding: 6px 9px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.82);
    color: var(--calendar-accent, #5B6EF5);
    font-size: 0.78rem;
    font-weight: 900;
    line-height: 1.2;
    white-space: nowrap;
`;

const PollSectionNote = styled.div`
    color: var(--calendar-text-soft, #6A7387);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 11px;
`;

const POLL_RESULTS_MODAL_WIDTH = "min(42.6667rem, calc(100vw - 2rem))";

const compactCountLabel = (count, singular, plural) => {
    const numericCount = Number(count || 0);
    return `${numericCount} ${numericCount === 1 ? singular : plural}`;
};

const formatPollDate = value => {
    if (!value) {
        return "Not set";
    }

    const date = moment(value);
    return date.isValid() ? date.format("MMM D, YYYY h:mm A") : "Not set";
};

const getCountdownLabel = value => {
    if (!value) {
        return "No end date";
    }

    const end = moment(value);
    if (!end.isValid()) {
        return "No end date";
    }

    if (hasPollEnded(value)) {
        return "Ended";
    }

    return `${end.from(moment(), true)} left`;
};

const hasPollEnded = value => {
    if (!value) {
        return false;
    }

    const end = moment(value).endOf("day");
    return end.isValid() && end.isSameOrBefore(moment());
};

const PollResultsModal = (props) => {
    const currentUser = useContext(UserContext);
    const controllerRef = useRef(new AbortController());

    const {
        showPollResults,
        onToggle,
        pollUuid
    } = props;

    const [loading, setLoading] = useState(true);
    const [pollDetails, setPollDetails] = useState([]);

    useEffect(() => {
        if (pollUuid !== "") {
            getPoll(pollUuid);
        }
    }, [pollUuid]);

    useEffect(() => {
        if (pollDetails && pollDetails.status === SurveyStatusType.ACTIVE) {
            const interval = setInterval(() => getPollRealTime(pollUuid), 10000);
            return () => clearInterval(interval);
        }

    }, [pollDetails]);

    /** to retrieve a specific poll, used in edit poll view */
    const getPoll = (pollUuid) => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }

        controllerRef.current = new AbortController();
        setLoading(true);
        let URL = `${API_URL}/polls/companyPolls/${pollUuid}`;
        
        axios.get(URL, {
            headers: { Authorization: `Bearer ${currentUser[UserKeys.TOKEN]}` },
            signal: controllerRef.current.signal
        }).then(res => {
            const pollData = res.data.poll;
            setPollDetails(pollData);
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.error(error.getApiErrorDetails().message);
            }
        }).finally(res => {
            setLoading(false);
        });
    };

    const getPollRealTime = (pollUuid) => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }

        controllerRef.current = new AbortController();
        let URL = `${API_URL}/polls/companyPolls/${pollUuid}`;
        
        axios.get(URL, {
            headers: { Authorization: `Bearer ${currentUser[UserKeys.TOKEN]}` },
            signal: controllerRef.current.signal
        }).then(res => {
            const pollData = res.data.poll;
            setPollDetails(pollData);
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.error(error.getApiErrorDetails().message);
            }
        });
    };

    const renderPollAnswerResults = (pollAnswers, isLoading) => {
        if (isLoading) {
            return (
                <>
                    {[0, 1, 2].map(item => (
                        <Skeleton key={item} width="100%" height="48px" animation="wave" />
                    ))}
                </>
            );
        }

        if (!pollAnswers || pollAnswers.length === 0) {
            return <PollSectionNote>No responses found.</PollSectionNote>;
        }

        return pollAnswers.map((pollAnswerItem, index) => (
            <AnswerOption
                key={pollAnswerItem.uuid || pollAnswerItem.value || index}
                $percentage={pollAnswerItem.percentage}
                $topVote={pollAnswerItem.isTopVote}
            >
                <AnswerNumber>{index + 1}</AnswerNumber>
                <AnswerText>{pollAnswerItem.value}</AnswerText>
                <PercentagePill>{pollAnswerItem.percentage}%</PercentagePill>
            </AnswerOption>
        ));
    };

    const renderPollMedia = () => {
        if (loading) {
            return (
                <PollMediaFrame>
                    <Skeleton variant="rect" width="100%" height="100%" animation="wave" />
                </PollMediaFrame>
            );
        }

        if (!pollImageUrl) {
            return null;
        }

        return (
            <PollMediaWrap>
                <PollMediaFrame>
                    <PollImage src={pollImageUrl} alt={pollDetails.question || "Poll"} />
                </PollMediaFrame>
                <CountdownBadge>{getCountdownLabel(pollEndDate)}</CountdownBadge>
            </PollMediaWrap>
        );
    };

    const handleCloseModal = () => {
        onToggle();
    };

    const pollImageUrl = pollDetails?.image?.imageUrl;
    const pollEndDate = pollDetails.endsAt || pollDetails.endDate || pollDetails.finishDate;
    const isPollEnded = hasPollEnded(pollEndDate);
    const headerAside = pollDetails.status === SurveyStatusType.ACTIVE ? <RealTimeBadge /> : null;

    return (
        <React.Fragment>
            <StandardModal
                open={showPollResults}
                onClose={handleCloseModal}
                title="Poll Results"
                headerAside={headerAside}
                widthMode={POLL_RESULTS_MODAL_WIDTH}
                bodyPadding="0"
                bodyGap="0"
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton type="button" onClick={handleCloseModal}>
                            Close
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                )}
            >
                <PollResultsContent>
                    <PollDetailSection>
                        {renderPollMedia()}
                        <PollContentStack>
                            <PollTitle>
                                {loading ? <Skeleton width="300px" height="26px" animation="wave" /> : pollDetails.question}
                            </PollTitle>
                            <PollEndDate>
                                {loading ? <Skeleton width="220px" height="18px" animation="wave" /> : `${isPollEnded ? "Ended" : "Ends"} ${formatPollDate(pollEndDate)}`}
                            </PollEndDate>
                            <VoteTally>
                                {loading ? <Skeleton width="90px" height="18px" animation="wave" /> : `${compactCountLabel(pollDetails.totalVotes, "vote", "votes")}${isPollEnded ? "" : " so far"}`}
                            </VoteTally>
                            <PollOptionsList>
                                {renderPollAnswerResults(pollDetails.options, loading)}
                            </PollOptionsList>
                            <PollSectionNote>Head to the app to answer this poll and submit your response.</PollSectionNote>
                        </PollContentStack>
                    </PollDetailSection>
                </PollResultsContent>
            </StandardModal>
        </React.Fragment>
    );
};

export default PollResultsModal;
